/**
 * Slim always-visible status bar at the top of the DevConsole. Six tiles
 * surface the things you'd otherwise have to click into a section to see:
 * cluster, connected wallet, treasury, current round, dev wallets, chat
 * + error counts. Tiles are read-only summaries; clicking them is not yet
 * wired (the section nav already covers that).
 *
 * Polling cadence: 5s for on-chain balances (treasury, connected wallet).
 * Anything backed by zustand re-renders for free on store change.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { deriveRoundPda } from "../games/last-touch/anchor";
import { useKothRound } from "../games/last-touch/useKothRound";
import { KOTH_ROUND_LS_KEY } from "../games/last-touch/useKothLobbyController";
import { useStore } from "../store";
import { deriveTreasuryPda } from "./session";
import { useDevErrorLog } from "./store/devErrorLog";
import { useDevWallets } from "./store/devWallets";
import { buildReadonlyProgram } from "../explorer/anchor";

function fmtSol(lamports: number | null | bigint | BN): string {
  if (lamports === null) return "—";
  let n: bigint;
  if (typeof lamports === "bigint") n = lamports;
  else if (typeof lamports === "number") n = BigInt(Math.floor(lamports));
  else n = BigInt(lamports.toString());
  const whole = n / BigInt(LAMPORTS_PER_SOL);
  const frac = n % BigInt(LAMPORTS_PER_SOL);
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 3)}`;
}

export function DevConsoleStatusBar(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [searchParams] = useSearchParams();
  const errors = useDevErrorLog((s) => s.entries);
  const devWallets = useDevWallets((s) => s.wallets);
  const chatters = useStore((s) => s.remotePlayers.size + (s.localPlayer ? 1 : 0));
  const chatCount = useStore((s) => s.chatLog.length);

  // Wallet balance (5s poll).
  const [walletLamports, setWalletLamports] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      if (!wallet.publicKey) {
        setWalletLamports(null);
      } else {
        try {
          const lp = await connection.getBalance(wallet.publicKey);
          if (!cancelled) setWalletLamports(lp);
        } catch {
          if (!cancelled) setWalletLamports(null);
        }
      }
      if (!cancelled) timer = setTimeout(tick, 5_000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [connection, wallet.publicKey]);

  // Treasury balance (5s poll).
  const programId = useMemo(() => buildReadonlyProgram(connection).programId, [connection]);
  const treasuryPda = useMemo(() => deriveTreasuryPda(programId), [programId]);
  const [treasuryLamports, setTreasuryLamports] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const info = await connection.getAccountInfo(treasuryPda);
        if (!cancelled) setTreasuryLamports(info?.lamports ?? 0);
      } catch {
        if (!cancelled) setTreasuryLamports(null);
      }
      if (!cancelled) timer = setTimeout(tick, 5_000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [connection, treasuryPda]);

  // Round (URL ?round=N → localStorage fallback).
  const roundIdParam = searchParams.get("round");
  const roundId = useMemo<BN | null>(() => {
    const raw =
      roundIdParam ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(KOTH_ROUND_LS_KEY)
        : null);
    if (!raw) return null;
    try {
      return new BN(raw, 10);
    } catch {
      return null;
    }
  }, [roundIdParam]);
  const { round } = useKothRound(roundId);
  // Side-effect: keep deriveRoundPda warm in dev so we surface bad ids early.
  void deriveRoundPda;

  const devWalletTotal = devWallets.reduce(
    (acc, w) => acc + (w.balanceLamports ?? 0),
    0,
  );

  const cluster =
    (import.meta.env.VITE_SOLANA_CLUSTER as string | undefined) ?? "(unset)";
  const clusterTone = cluster.startsWith("mainnet") ? "#f47a8e" : "#7af09e";

  return (
    <div style={barStyle}>
      <Tile label="cluster" value={cluster} tone={clusterTone} />
      <Tile
        label="wallet"
        value={
          wallet.publicKey
            ? `${wallet.publicKey.toBase58().slice(0, 4)}…${wallet.publicKey.toBase58().slice(-4)} · ${fmtSol(walletLamports)} SOL`
            : "(disconnected)"
        }
      />
      <Tile
        label="treasury"
        value={`${fmtSol(treasuryLamports)} SOL`}
      />
      <Tile
        label="round"
        value={
          round
            ? `#${round.roundId.toString()} · pot ${fmtSol(round.potLamports)} SOL · ${round.touchCount}t`
            : roundId
              ? `#${roundId.toString()} (no PDA)`
              : "(none)"
        }
      />
      <Tile
        label="dev wallets"
        value={`${devWallets.length} · ${fmtSol(devWalletTotal)} SOL`}
      />
      <Tile
        label="chat"
        value={`${chatters} live · ${chatCount} msgs`}
      />
      <Tile
        label="errors"
        value={`${errors.length}`}
        tone={errors.length > 0 ? "#f47a8e" : "#7a8098"}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}): JSX.Element {
  return (
    <div style={tileStyle}>
      <div style={tileLabelStyle}>{label}</div>
      <div style={{ ...tileValueStyle, color: tone ?? "#e8e8ef" }}>{value}</div>
    </div>
  );
}

const barStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: 1,
  background: "#1c1c26",
  borderBottom: "1px solid #1c1c26",
  flex: "0 0 auto",
};

const tileStyle: React.CSSProperties = {
  background: "#0e0e15",
  padding: "6px 8px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
};

const tileLabelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: "#5a5a72",
  textTransform: "uppercase",
};

const tileValueStyle: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
